import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [linkError, setLinkError] = useState('');

  // When the member clicks the email link, Supabase (implicit flow) puts the
  // recovery token in the URL fragment. Because the app uses HashRouter the URL
  // is a double-hash, e.g.
  //   /#/reset-password#access_token=...&refresh_token=...&type=recovery
  // so we pull the token portion out by hand and establish the session, rather
  // than relying on automatic detection (which the double-hash can defeat).
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setSessionReady(true);
      }
    });

    (async () => {
      const raw = window.location.hash || '';

      // Supabase returns expired / already-used / invalid links as an error in
      // the fragment (e.g. #error=access_denied&error_code=otp_expired...).
      // Surface that clearly instead of leaving the page spinning forever.
      const errIdx = raw.indexOf('error');
      if (raw.indexOf('error_code=') !== -1 || (errIdx !== -1 && raw.indexOf('error=') !== -1)) {
        const params = new URLSearchParams(raw.substring(errIdx));
        const code = params.get('error_code') || '';
        window.history.replaceState(null, '', '#/reset-password');
        if (code === 'otp_expired') {
          setLinkError('This reset link has expired. Please request a new one.');
        } else {
          setLinkError('This reset link is invalid or has already been used. Please request a new one.');
        }
        return;
      }

      const idx = raw.indexOf('access_token');
      if (idx !== -1) {
        const params = new URLSearchParams(raw.substring(idx));
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          if (!error) {
            setSessionReady(true);
            // Strip the token out of the URL so it isn't left sitting in history.
            window.history.replaceState(null, '', '#/reset-password');
            return;
          }
          setLinkError('This reset link has expired or already been used. Please request a new one.');
          return;
        }
      }
      // Fallback: a session may already be active (event fired before mount).
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionReady(true);
      } else {
        // No token in the URL and no active session: there's nothing to verify,
        // so don't leave the member staring at an endless "Verifying…" spinner.
        setLinkError('This password reset link is invalid or has expired. Please request a new one.');
      }
    })();

    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords don\u2019t match');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      // After 2 seconds, sign out and redirect to login so user logs in fresh
      setTimeout(async () => {
        await supabase.auth.signOut();
        nav('/login');
      }, 2000);
    }
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#1A1A1A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 400, color: '#fff', letterSpacing: '0.04em' }}>team <span style={{ fontWeight: 800 }}>breakthrough</span></div>
        <div style={{ color: '#FF6200', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em', marginTop: 6 }}>BOX · BATH · CORSHAM</div>
      </div>

      {/* Card */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 380 }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Set new password</h2>

        {success ? (
          <div>
            <div style={{ background: '#EAFAF0', border: '1px solid #10B981', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#10B981' }}>✓ Password updated</div>
              <div style={{ fontSize: '0.85rem', color: '#444', lineHeight: 1.5 }}>
                Redirecting you to the login screen…
              </div>
            </div>
          </div>
        ) : !sessionReady ? (
          <div>
            {linkError ? (
              <>
                <div className="alert alert-error">{linkError}</div>
                <a href="/#/forgot-password" className="btn btn-primary btn-full" style={{ marginTop: 8, textDecoration: 'none', textAlign: 'center' }}>
                  Request a new link
                </a>
                <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.85rem' }}>
                  <a href="/#/login" style={{ color: '#666', fontWeight: 500 }}>← Back to login</a>
                </p>
              </>
            ) : (
              <>
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 20, lineHeight: 1.5 }}>
                  Verifying your reset link…
                </p>
                <div className="loading-center"><div className="spinner" /></div>
                <p style={{ fontSize: '0.8rem', color: '#888', textAlign: 'center', marginTop: 24, lineHeight: 1.5 }}>
                  If this screen doesn't progress after a few seconds, your reset link may have expired. Request a new one from the <a href="/#/forgot-password" style={{ color: '#FF6200', fontWeight: 600 }}>forgot password page</a>.
                </p>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 20, lineHeight: 1.5 }}>
              Choose a new password for your account. Minimum 8 characters.
            </p>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">New password</label>
              <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" minLength={8} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <input className="form-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required autoComplete="new-password" minLength={8} />
            </div>
            <button className="btn btn-primary btn-full" type="submit" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
